import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as api from "../api/client";
import { setSocketToken } from "../api/rooms";
import { pixelTheme } from "../theme/pixel";
import { defaultBtn, withBtnStyle } from "../theme/styles";

type State = "pending" | "ok" | "bad";

export function VerifyEmailPage() {
  const theme = pixelTheme;
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [state, setState] = useState<State>("pending");

  // React 18 StrictMode runs effects twice in dev. The verify
  // endpoint is single-use: the second call hits an already-consumed
  // token and 401s, overwriting the success state from the first
  // call. Guard with a ref so the POST fires exactly once per token
  // value, regardless of how many times the effect re-runs.
  const submitted = useRef<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState("bad");
      return;
    }

    if (submitted.current === token) return;
    submitted.current = token;

    (async () => {
      try {
        const resp = await api.verifyEmail(token);
        if ("user" in resp) setSocketToken(resp.socket_token);
        setState("ok");
      } catch {
        setState("bad");
      }
    })();
  }, [token]);

  return (
    <div
      style={{
        minHeight: "100%",
        background: theme.pageBg,
        color: theme.ink,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.fontBody,
        padding: 20,
      }}
    >
      <div
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdgeStrong}`,
          padding: 28,
          width: "100%",
          maxWidth: 460,
          display: "grid",
          gap: 18,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 16,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
            textAlign: "center",
          }}
        >
          {state === "pending"
            ? "VERIFYING…"
            : state === "ok"
              ? "EMAIL VERIFIED"
              : "VERIFICATION FAILED"}
        </div>

        {state === "pending" && (
          <div style={{ fontSize: 14, color: theme.inkDim, textAlign: "center" }}>
            One moment.
          </div>
        )}

        {state === "ok" && (
          <>
            <div style={{ fontSize: 14, color: theme.inkDim, lineHeight: 1.5 }}>
              Thanks — your email is now confirmed.
            </div>
            <Link to="/lobby" {...withBtnStyle(defaultBtn(theme, false), { fontSize: 12, padding: "10px 16px", textAlign: "center" })}>
              CONTINUE
            </Link>
          </>
        )}

        {state === "bad" && (
          <>
            <div style={{ fontSize: 14, color: theme.inkDim, lineHeight: 1.5 }}>
              This verification link is invalid or has expired. Request
              a new one — type your email on the next page.
            </div>
            <Link to="/check-your-email" {...withBtnStyle(defaultBtn(theme, false), { fontSize: 12, padding: "10px 16px", textAlign: "center" })}>
              GET A NEW LINK
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
